import type { CriteriaRule, CriteriaSignals, ThreatAssessment } from './types'
import { highestLevel, levelRank, threatLabel, type ThreatLevel } from './levels'

/**
 * ── Activation-criteria engine (Milestone 2 seam, with a working M1 stub) ─────
 *
 * `evaluateCriteria(signals)` is the single entry point. Today it runs two rules
 * (NWS alerts + SPC); Milestone 2 registers more rules (tropical proximity, model
 * thresholds) by pushing onto DEFAULT_RULES — nothing downstream changes.
 *
 * The result drives: the Home status banner, the ambient glow (via the threat
 * store), and — once the level crosses STORM_MODE_ACTIVATION — Storm Mode.
 */

/** Level at/above which Storm Mode auto-activates. Tunable per Milestone 2. */
export const STORM_MODE_ACTIVATION: ThreatLevel = 'severe'

// ── Milestone 1 rules ────────────────────────────────────────────────────────
const alertsRule: CriteriaRule = {
  id: 'nws-alerts',
  describe: 'Highest active NWS alert level for the location',
  evaluate: (s) =>
    s.alertsLevel && levelRank(s.alertsLevel) > levelRank('quiet')
      ? { source: 'nws-alerts', detail: `${threatLabel(s.alertsLevel)}-level NWS alert active`, level: s.alertsLevel }
      : null,
}

const spcRule: CriteriaRule = {
  id: 'spc-outlook',
  describe: "Today's SPC convective outlook for the area",
  evaluate: (s) =>
    s.spcLevel && levelRank(s.spcLevel) >= levelRank('watch')
      ? { source: 'spc-outlook', detail: `SPC ${threatLabel(s.spcLevel)} severe risk today`, level: s.spcLevel }
      : null,
}

// ── Milestone 2 plug-in points (left intentionally unregistered) ─────────────
//
// const tropicalRule: CriteriaRule = { id: 'tropical-proximity', ... }   // uses signals.tropicalProximityMi
// const capeRule:      CriteriaRule = { id: 'model-cape',          ... }   // uses signals.modelFields.cape
// Register them by adding to DEFAULT_RULES below; evaluateCriteria needs no change.

export const DEFAULT_RULES: CriteriaRule[] = [alertsRule, spcRule]

export function evaluateCriteria(
  signals: CriteriaSignals,
  rules: CriteriaRule[] = DEFAULT_RULES,
): ThreatAssessment {
  const reasons = rules.map((r) => r.evaluate(signals)).filter((r): r is NonNullable<typeof r> => r !== null)
  const level = highestLevel(reasons.map((r) => r.level))
  return {
    level,
    reasons,
    stormModeActive: levelRank(level) >= levelRank(STORM_MODE_ACTIVATION),
    // Caller stamps the real time (engine stays pure / SSR-safe).
    evaluatedAt: '',
  }
}
